import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { compose } from 'recompose'
import classNames from 'classnames'
import { withTranslatedMeta, withRouter } from 'i18n/utils'
import Button from '@components/Button'
import ErrorMessage from '@components/ErrorMessage'
import useUnsavedChangesWarning from 'shared/hooks/useUnsavedChangesWarning'
import {
    isOnDemandParkingActivatedSelector,
    draftEnabledSelector,
    draftSelectedVehicleIdsSelector,
    draftSelectedCardIdSelector,
    canActivateSelector,
    canSaveSelector,
    isDirtySelector,
    onDemandActivatePendingSelector,
    onDemandSavePendingSelector,
    onDemandParkingErrorSelector,
} from '../selectors'
import * as onDemandActions from '../actions'
import VehiclesList from './VehiclesList'
import EnableOnDemandAccessService from './EnableOnDemandAccessService'
import PaymentMethodsList from './PaymentMethodsList'
import OnDemandParkingIntroModal from './OnDemandParkingIntroModal'
import OnDemandParkingActivateModal from './OnDemandParkingActivateModal'
import OnDemandParkingActivatedModal from './OnDemandParkingActivatedModal'
import styles from './styles.module.scss'

const OnDemandParking = ({
    isOnDemandParkingActivated,
    draftEnabled,
    selectedVehicleIds,
    selectedCardId,
    canActivate,
    canSave,
    hasUnsavedChanges,
    activatePending,
    savePending,
    error,
    initOnDemandParking,
    toggleVehicle,
    selectCard,
    confirmActivate,
    saveChanges,
    setOnDemandEnabled,
}) => {
    const [showActivateModal, setShowActivateModal] = useState(false)

    useEffect(() => {
        initOnDemandParking()
    }, [])

    const onConfirmActivate = () => {
        setShowActivateModal(false)
        confirmActivate()
    }

    useUnsavedChangesWarning(hasUnsavedChanges)

    return (
        <>
            <div className={classNames(styles.onDemandParkingContainer, 'container', 'dashboard-inner-container')}>
                {isOnDemandParkingActivated && (
                    <>
                        <EnableOnDemandAccessService
                            checked={draftEnabled}
                            onChange={() => setOnDemandEnabled(!draftEnabled)}
                        />
                        <div className={styles.horizontalLine} />
                    </>
                )}
                <h3 className="font-weight-bold">
                    <T>dashboard.menu.vehicles</T>
                </h3>
                <VehiclesList selectedVehicleIds={selectedVehicleIds} onToggle={toggleVehicle} />
                <div className={styles.horizontalLine} />
                <h3 className="font-weight-bold mt-4">
                    <T>dashboard.on.demand.parking.payment.method</T>
                </h3>
                <PaymentMethodsList selectedCardId={selectedCardId} onSelectCard={selectCard} />
                <div className={styles.horizontalLine} />
                {isOnDemandParkingActivated && (
                    <Button
                        className={styles.submitButton}
                        isLoading={savePending === true}
                        disabled={!canSave || savePending === true}
                        onClick={saveChanges}>
                        <T>dashboard.on.demand.parking.save.changes</T>
                    </Button>
                )}
                {!isOnDemandParkingActivated && (
                    <Button
                        className={styles.submitButton}
                        isLoading={activatePending === true}
                        disabled={!canActivate || activatePending === true}
                        onClick={() => setShowActivateModal(true)}>
                        <T>dashboard.on.demand.parking.activate</T>
                    </Button>
                )}
                {error && <ErrorMessage className="mb-3" messages={[error.message || String(error)]} />}
            </div>
            {showActivateModal && (
                <OnDemandParkingActivateModal
                    onCancel={() => setShowActivateModal(false)}
                    onConfirm={onConfirmActivate}
                />
            )}
            <OnDemandParkingIntroModal initialShowModal={!isOnDemandParkingActivated} />
            <OnDemandParkingActivatedModal isActivated={isOnDemandParkingActivated} />
        </>
    )
}

const mapStateToProps = createStructuredSelector({
    isOnDemandParkingActivated: isOnDemandParkingActivatedSelector,
    draftEnabled: draftEnabledSelector,
    selectedVehicleIds: draftSelectedVehicleIdsSelector,
    selectedCardId: draftSelectedCardIdSelector,
    canActivate: canActivateSelector,
    canSave: canSaveSelector,
    hasUnsavedChanges: isDirtySelector,
    activatePending: onDemandActivatePendingSelector,
    savePending: onDemandSavePendingSelector,
    error: onDemandParkingErrorSelector,
})

const mapDispatchToProps = {
    initOnDemandParking: onDemandActions.initOnDemandParking,
    toggleVehicle: onDemandActions.toggleOnDemandVehicle,
    selectCard: onDemandActions.setOnDemandCard,
    confirmActivate: onDemandActions.activateOnDemandRequest,
    saveChanges: onDemandActions.saveOnDemandChangesRequest,
    setOnDemandEnabled: onDemandActions.setOnDemandEnabled,
}

export default compose(
    withTranslatedMeta('dashboard.menu.on.demand'),
    connect(mapStateToProps, mapDispatchToProps)
)(OnDemandParking)
